import logger from './logger';

const SITE_URL = 'https://goodsong.club';

export const getCanonicalSongUrl = (songId) =>
  `${SITE_URL}/song/${encodeURIComponent(songId)}`;

// Shared-link URL. The `shared=1` flag is a presentation hint that tells the
// app to render the standalone landing view (focused player + "Open in app"
// CTA) instead of the in-app multi-panel layout. Crawlers and canonical URLs
// still point at the plain /song/:id form.
export const getSharedSongUrl = (songId) =>
  `${getCanonicalSongUrl(songId)}?shared=1`;

const legacyCopy = (text) => {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (err) {
    logger.error('[shareSong] legacy copy failed', err);
    return false;
  }
};

/**
 * Share a song using the native share sheet when available, otherwise copy the
 * canonical song URL to the clipboard. Always uses the canonical /song/:id URL
 * (not the in-app `?songId=` form) so shared links are stable.
 *
 * @returns {Promise<'shared' | 'copied' | 'cancelled' | 'failed'>}
 */
export async function shareSong(song) {
  if (!song?.id) return 'failed';

  const url = getSharedSongUrl(song.id);
  const title =
    song.songName && song.songArtist
      ? `${song.songName} by ${song.songArtist}`
      : song.songName || 'A good song';
  const text = `Check out ${title} on Good Song Club`;

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
      logger.warn('[shareSong] navigator.share failed, falling back to clipboard', err);
    }
  }

  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch (err) {
      logger.warn('[shareSong] clipboard.writeText failed, trying legacy copy', err);
    }
  }

  return legacyCopy(url) ? 'copied' : 'failed';
}
